import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useParams } from 'react-router';
import { Button } from '@saucelabs/ds-button';
import { Typography } from '@saucelabs/ds-typography';
import {
  IconCheckmarkCircleFill,
  IconEndFill,
  IconLiveTimeFill,
  IconWebBrokenFill,
} from '@saucelabs/ds-icons';

import { GlobalState } from '../../../reducers';
import { ValueOf } from '../../../globalTypes';
import { mobileSessionSelector } from '../../../selectors';
import {
  CONFIGURATION_DEVICE_TYPES,
  CONFIGURATION_TEST_TYPES,
  SESSION_INACTIVE_REASONS,
} from '../../../constants';
import { cleanManualPath } from '../../../actions';
import { selectors as testConfigSelectors } from '../../testConfiguration';
import ChangeTestData from './ChangeTestData';
import selectors from '../selectors';
import {
  EVENTS,
  LIVE_TESTING_TRACKING_PROPS,
  LIVE_TESTING_PRODUCT_SUB_AREAS,
  LIVE_TESTING_SUBJECT_NAMES,
  trackEvent,
} from '../../../tracking';

type SessionInactiveContent = {
  copy: string;
  headline: string;
  statusIcon: JSX.Element;
  buttonPrimary?: {
    label: string;
    onClickHandler: () => void;
  }
  buttonSecondary?: {
    label: string;
    onClickHandler: () => void;
  }
}

type SessionInactiveProps = {
  isActiveFreeUser: boolean;
  reason: keyof typeof SESSION_INACTIVE_REASONS;
}

const CONFIGURATIONS = Object.freeze({
  [SESSION_INACTIVE_REASONS.CLOSED_BY_USER]: {
    copy: 'You can see details and watch the recording in the Test Results page.',
    headline: 'Your test session has ended.',
    statusIcon: <IconCheckmarkCircleFill className="h-full w-full fill-current text-green-600" />,
  },
  [SESSION_INACTIVE_REASONS.EXPIRED]: {
    copy: 'You have reached a maximum session duration.',
    headline: 'Your test session has ended.',
    statusIcon: <IconLiveTimeFill className="h-full w-full fill-current text-orange-600" />,
  },
  [SESSION_INACTIVE_REASONS.INACTIVE]: {
    copy: 'You can now look at the test session results.',
    headline: 'This session is no longer active.',
    statusIcon: <IconEndFill className="h-full w-full fill-current text-orange-600" />,
  },
  [SESSION_INACTIVE_REASONS.RECONNECTION_FAILED]: {
    copy: 'You can view test results or end or start a new test session.',
    headline: 'We couldn’t reconnect to your live session.',
    statusIcon: <IconWebBrokenFill className="h-full w-full fill-current text-gray-500" />,
  },
  [SESSION_INACTIVE_REASONS.UNKNOWN]: {
    copy: 'We were unable to continue your session due to a service issue.',
    headline: 'This session is no longer active.',
    statusIcon: <IconEndFill className="h-full w-full fill-current text-orange-600" />,
  },
});

const SessionInactive: React.FC<SessionInactiveProps> = ({ isActiveFreeUser, reason }) => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const { sessionId, deviceId } = useParams<{ sessionId?: string, deviceId?: string }>();
  const deviceType = useSelector<GlobalState, keyof typeof CONFIGURATION_DEVICE_TYPES>(
    testConfigSelectors.deviceTypeSelector,
  );
  const testType = useSelector<GlobalState, ValueOf<typeof CONFIGURATION_TEST_TYPES>>(
    testConfigSelectors.testTypeSelector,
  );
  const testId = useSelector(selectors.testIdSelector);
  const isRdc = (deviceType === CONFIGURATION_DEVICE_TYPES.REAL_DEVICE) || !!deviceId;
  const isAppTest = testType === CONFIGURATION_TEST_TYPES.APP;
  const { session: mobileSession } = useSelector(mobileSessionSelector);
  const content: SessionInactiveContent = { ...CONFIGURATIONS[reason] };

  const viewTestResults = () => {
    const rdcTestReportId = mobileSession.get('testReportId') as string;
    const testReportId = isRdc ? rdcTestReportId : sessionId;

    trackEvent(EVENTS.OBJECT_CLICKED, {
      subject_name: LIVE_TESTING_SUBJECT_NAMES.END_SESSION_REWATCH,
      product_sub_area: LIVE_TESTING_PRODUCT_SUB_AREAS.END_SESSION_SCREEN,
      ...LIVE_TESTING_TRACKING_PROPS,
    });

    if (testReportId) {
      return dispatch(push(`/tests/${testReportId}`));
    }

    return dispatch(push(isRdc ? '/dashboard/live/rdc' : '/dashboard/live/vdc'));
  };

  const startNewTest = () => {
    trackEvent(EVENTS.OBJECT_CLICKED, {
      subject_name: LIVE_TESTING_SUBJECT_NAMES.END_SESSION_START_NEW_TEST,
      product_sub_area: LIVE_TESTING_PRODUCT_SUB_AREAS.END_SESSION_SCREEN,
      ...LIVE_TESTING_TRACKING_PROPS,
    });

    return dispatch(cleanManualPath(isAppTest));
  };

  if (reason === SESSION_INACTIVE_REASONS.EXPIRED && isActiveFreeUser) {
    content.copy = `${content.copy}\nWant longer sessions? Upgrade your plan.`;
    content.buttonPrimary = {
      label: 'Upgrade Plan',
      onClickHandler: () => { dispatch(push('/billing')); },
    };
    content.buttonSecondary = {
      label: 'View Test Results',
      onClickHandler: viewTestResults,
    };
  }

  // Since this component handles multiple cases but renaming test only applies to one of them
  // it's easier to wrap this case in a separate return
  // let's incorporate once it's clear on how to proceed
  if (reason === SESSION_INACTIVE_REASONS.CLOSED_BY_USER && testId) {
    return (
      <div
        className="h-screen w-screen bg-gray-300 flex flex-row justify-center items-center"
        data-qa="session-inactive-wrapper"
      >
        <div className="relative w-full max-w-76 pt-8 pb-5 px-3 mx-1 bg-white flex flex-col items-center justify-center text-center">
          <div className="h-2.5 w-2.5">{content.statusIcon}</div>

          <Typography
            className="mt-3 mb-2"
            variant="heading-4"
          >
            {content.headline}
          </Typography>

          <ChangeTestData />

          <Button
            data-qa="session-inactive-link-to-new-test"
            onPress={content.buttonSecondary?.onClickHandler || startNewTest}
            variant="outline"
          >
            {content.buttonSecondary?.label || 'Start New Test'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen bg-gray-300 flex flex-row justify-center items-center"
      data-qa="session-inactive-wrapper"
    >
      <div className="relative w-full max-w-76 pt-8 pb-5 px-3 mx-1 bg-white flex flex-col items-center justify-center text-center">
        <div className="h-2.5 w-2.5">{content.statusIcon}</div>

        <Typography
          className="mt-3 mb-2"
          variant="heading-4"
        >
          {content.headline}
        </Typography>

        <Typography
          className="whitespace-pre-line"
          variant="body-large"
        >
          {content.copy}
        </Typography>

        <Button
          className="mt-5"
          data-qa="session-inactive-link-to-test-results"
          onPress={content.buttonPrimary?.onClickHandler || viewTestResults}
          variant="contained"
        >
          {content.buttonPrimary?.label || 'View Test Results'}
        </Button>

        <Button
          className="mt-2"
          data-qa="session-inactive-link-to-new-test"
          onPress={content.buttonSecondary?.onClickHandler || startNewTest}
          variant="text"
        >
          {content.buttonSecondary?.label || 'Start New Test'}
        </Button>
      </div>
    </div>
  );
};

export default SessionInactive;
